import { useEffect, useState, useCallback } from 'react';
import { GoogleMap, Polyline, Marker, InfoWindow } from '@react-google-maps/api';
import { useGoogleMaps } from '@/hooks/useGoogleMaps';
import { Card } from '@/components/ui/card';
import { Badge } from '@/components/ui/badge';
import { Loader2 } from 'lucide-react';
import { logger } from '@/lib/logger';
import { MapFallback } from './MapFallback';
import { StravaStyleMarker } from './StravaStyleMarker';
import { darkMapStyles } from '@/lib/mapStyles';
import { useTrailColor } from '@/hooks/useTrailColor';

interface TrackingPoint {
  lat: number;
  lng: number;
  timestamp: number;
  speed?: number;
  pace?: number;
  altitude?: number;
}

interface PointOfInterest {
  position: { lat: number; lng: number };
  type: 'pr' | 'hydration' | 'music_boost' | 'milestone';
  title: string;
  description: string;
  icon: string;
}

interface EnhancedRunMapProps {
  points: TrackingPoint[];
  currentPosition?: { lat: number; lng: number };
  paceTarget?: number; // min/km
  showHeatmap?: boolean;
  showPOI?: boolean;
  className?: string;
  height?: string;
  avatarUrl?: string;
  currentAccuracy?: number; // GPS accuracy in meters
}

export const EnhancedRunMap = ({
  points,
  currentPosition,
  paceTarget,
  showHeatmap = true,
  showPOI = true,
  className,
  height = '400px',
  avatarUrl,
  currentAccuracy
}: EnhancedRunMapProps) => {
  const { isLoaded, loadError } = useGoogleMaps();
  const { trailColor } = useTrailColor();
  const [map, setMap] = useState<google.maps.Map | null>(null);
  const [selectedPOI, setSelectedPOI] = useState<PointOfInterest | null>(null);
  const [pointsOfInterest, setPointsOfInterest] = useState<PointOfInterest[]>([]);

  const mapContainerStyle = {
    width: '100%',
    height
  };

  const center = currentPosition || 
    (points.length > 0 ? { lat: points[0].lat, lng: points[0].lng } : 
    { lat: -23.5505, lng: -46.6333 }); // São Paulo default

  const mapOptions: google.maps.MapOptions = {
    disableDefaultUI: false,
    zoomControl: true,
    mapTypeControl: false,
    streetViewControl: false,
    fullscreenControl: false,
    styles: darkMapStyles
  };

  // Tratar erro de carregamento
  if (loadError) {
    logger.error('EnhancedRunMap load error', loadError);
    return (
      <Card className={className}>
        <div className="h-64">
          <MapFallback error="Erro ao carregar mapa interativo. Seus dados estão salvos." />
        </div>
      </Card>
    );
  }

  // Calcular heatmap colors baseado no pace
  const getPathColor = useCallback((pace?: number) => {
    if (!showHeatmap || !pace || !paceTarget) {
      return trailColor; // User's preferred trail color
    }

    const diff = pace - paceTarget;
    
    // Verde: pace ideal (±10% do target)
    if (Math.abs(diff) <= paceTarget * 0.1) {
      return '#10B981'; // Green
    }
    
    // Amarelo: desacelerando (10-30% mais lento)
    if (diff > paceTarget * 0.1 && diff <= paceTarget * 0.3) {
      return '#F59E0B'; // Yellow
    }
    
    // Vermelho: muito lento (>30% mais lento)
    if (diff > paceTarget * 0.3) {
      return '#EF4444'; // Red
    }
    
    // Ciano: muito rápido
    return '#06B6D4'; // Cyan
  }, [showHeatmap, paceTarget]);

  // Dividir pontos em segmentos para heatmap
  const pathSegments = useCallback(() => {
    if (points.length < 2) return [];

    const segments: Array<{
      path: { lat: number; lng: number }[];
      color: string;
      pace?: number;
    }> = [];

    for (let i = 0; i < points.length - 1; i++) {
      const point1 = points[i];
      const point2 = points[i + 1];
      
      segments.push({
        path: [
          { lat: point1.lat, lng: point1.lng },
          { lat: point2.lat, lng: point2.lng }
        ],
        color: getPathColor(point1.pace),
        pace: point1.pace
      });
    }

    return segments;
  }, [points, getPathColor]);

  // Detectar Points of Interest automaticamente
  useEffect(() => {
    if (points.length < 10 || !showPOI) return;

    const pois: PointOfInterest[] = [];

    // 1. Detectar PR de pace (segmento mais rápido)
    let fastestPace = Infinity;
    let fastestIndex = -1;
    
    points.forEach((point, i) => {
      if (point.pace && point.pace < fastestPace) {
        fastestPace = point.pace;
        fastestIndex = i;
      }
    });

    if (fastestIndex >= 0 && fastestPace < 10) { // pace válido
      pois.push({
        position: { lat: points[fastestIndex].lat, lng: points[fastestIndex].lng },
        type: 'pr',
        title: '🏆 PR de Pace!',
        description: `Seu melhor ritmo: ${fastestPace.toFixed(2)} min/km`,
        icon: '🏆'
      });
    }

    // 2. Milestones de distância (a cada 5km)
    const EARTH_RADIUS = 6371; // km
    let totalDistance = 0;
    let lastMilestone = 0;

    for (let i = 1; i < points.length; i++) {
      const prev = points[i - 1];
      const curr = points[i];
      
      // Haversine distance
      const dLat = (curr.lat - prev.lat) * Math.PI / 180;
      const dLng = (curr.lng - prev.lng) * Math.PI / 180;
      const a = Math.sin(dLat / 2) * Math.sin(dLat / 2) +
                Math.cos(prev.lat * Math.PI / 180) * Math.cos(curr.lat * Math.PI / 180) *
                Math.sin(dLng / 2) * Math.sin(dLng / 2);
      const c = 2 * Math.atan2(Math.sqrt(a), Math.sqrt(1 - a));
      totalDistance += EARTH_RADIUS * c;

      // Milestone a cada 5km
      if (totalDistance - lastMilestone >= 5) {
        pois.push({
          position: { lat: curr.lat, lng: curr.lng },
          type: 'milestone',
          title: `🎯 ${Math.floor(totalDistance)}km`,
          description: `Milestone alcançado!`,
          icon: '🎯'
        });
        lastMilestone = Math.floor(totalDistance / 5) * 5;
      }
    }

    // 3. Hidratação sugerida (a cada 20min ou 3km)
    const HYDRATION_INTERVAL = 20 * 60 * 1000; // 20 min
    const firstPoint = points[0];
    
    points.forEach((point, i) => {
      if (i === 0) return;
      
      const timeSinceStart = point.timestamp - firstPoint.timestamp;
      
      if (timeSinceStart % HYDRATION_INTERVAL < 10000 && i > points.length * 0.3) {
        pois.push({
          position: { lat: point.lat, lng: point.lng },
          type: 'hydration',
          title: '💧 Hidratação',
          description: 'Ponto sugerido para hidratação',
          icon: '💧'
        });
      }
    });

    setPointsOfInterest(pois.slice(0, 8)); // Max 8 POIs
    
    logger.info('POIs detected', {
      component: 'EnhancedRunMap',
      count: pois.length,
      types: pois.map(p => p.type)
    });
  }, [points, showPOI]);

  const onLoad = useCallback((map: google.maps.Map) => {
    setMap(map);
    
    // Fit bounds to show entire route
    if (points.length > 1) {
      const bounds = new google.maps.LatLngBounds();
      points.forEach(point => {
        bounds.extend(new google.maps.LatLng(point.lat, point.lng));
      });
      map.fitBounds(bounds);
    }
  }, [points]);

  if (!isLoaded) {
    return (
      <Card className={className}>
        <div className="flex items-center justify-center h-64 animate-fade-in">
          <Loader2 className="w-8 h-8 animate-spin text-muted-foreground" />
        </div>
      </Card>
    );
  }

  return (
    <Card className={`${className} animate-fade-in animate-scale-in`}>
      <GoogleMap
        mapContainerStyle={mapContainerStyle}
        center={center}
        zoom={15}
        onLoad={onLoad}
        options={mapOptions}
      >
        {/* Polyline with heatmap colors - Strava style (thinner) */}
        {showHeatmap ? (
          pathSegments().map((segment, i) => (
            <Polyline
              key={i}
              path={segment.path}
              options={{
                strokeColor: segment.color,
                strokeOpacity: 0.8,
                strokeWeight: 4,
              }}
            />
          ))
        ) : (
          <Polyline
            path={points.map(p => ({ lat: p.lat, lng: p.lng }))}
            options={{
              strokeColor: trailColor,
              strokeOpacity: 0.8,
              strokeWeight: 4,
            }}
          />
        )}

        {/* Current position marker - Strava style animated */}
        {currentPosition && (
          <StravaStyleMarker
            position={currentPosition}
            accuracy={currentAccuracy}
            color={trailColor}
          />
        )}

        {/* Points of Interest */}
        {showPOI && pointsOfInterest.map((poi, i) => (
          <Marker
            key={i}
            position={poi.position}
            onClick={() => setSelectedPOI(poi)}
            icon={{
              path: google.maps.SymbolPath.CIRCLE,
              scale: 10,
              fillColor: poi.type === 'pr' ? '#10B981' :
                        poi.type === 'hydration' ? '#3B82F6' :
                        poi.type === 'music_boost' ? '#A855F7' : '#F59E0B',
              fillOpacity: 0.9,
              strokeColor: '#FFFFFF',
              strokeWeight: 2,
            }}
            label={{
              text: poi.icon,
              fontSize: '16px',
            }}
          />
        ))}

        {/* InfoWindow for selected POI */}
        {selectedPOI && (
          <InfoWindow
            position={selectedPOI.position}
            onCloseClick={() => setSelectedPOI(null)}
          >
            <div className="p-2">
              <h3 className="font-semibold text-sm">{selectedPOI.title}</h3>
              <p className="text-xs text-muted-foreground mt-1">
                {selectedPOI.description}
              </p>
            </div>
          </InfoWindow>
        )}
      </GoogleMap>

      {/* Legend */}
      {showHeatmap && paceTarget && (
        <div className="p-3 border-t space-y-2">
          <h4 className="text-xs font-semibold text-muted-foreground">Legenda de Pace:</h4>
          <div className="flex flex-wrap gap-3 text-xs">
            <div className="flex items-center gap-2">
              <div className="w-3 h-3 rounded-full bg-green-500" />
              <span>Ideal (~{paceTarget.toFixed(2)})</span>
            </div>
            <div className="flex items-center gap-2">
              <div className="w-3 h-3 rounded-full bg-orange-500" />
              <span>Desacelerando</span>
            </div>
            <div className="flex items-center gap-2">
              <div className="w-3 h-3 rounded-full bg-red-500" />
              <span>Muito lento</span>
            </div>
            <div className="flex items-center gap-2">
              <div className="w-3 h-3 rounded-full bg-cyan-500" />
              <span>Muito rápido</span>
            </div>
          </div>
        </div>
      )}

      {/* POI Summary */}
      {showPOI && pointsOfInterest.length > 0 && (
        <div className="p-3 border-t">
          <div className="flex flex-wrap gap-2">
            {pointsOfInterest.slice(0, 4).map((poi, i) => (
              <Badge key={i} variant="secondary" className="text-xs">
                {poi.icon} {poi.title}
              </Badge>
            ))}
          </div>
        </div>
      )}
    </Card>
  );
};
